/**
 * ForcedClosureDamagesSection — accountant-dashboard view of the persisted phased
 * forced-closure result (engine_id='fc_phased'), the authoritative, court-facing damages
 * computed by the deterministic phased engine during analysis.
 *
 * Reuses the same PhasedDamagesBreakdown the wizard uses. Self-gating: if the case has no
 * persisted fc_phased output (non-FC or not yet analyzed) the endpoint 404s and the section
 * renders nothing, so it only appears for forced-closure cases.
 */
import React, { useEffect, useState } from 'react';
import axios from '@/lib/http';
import { Scale } from 'lucide-react';
import { useAuth } from '../../contexts/TestAuthContext';
import type { PhasedPreviewResult } from '../../services/futureClosureApi';
import PhasedDamagesBreakdown from '../wizard/v2/PhasedDamagesBreakdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface Props {
  reportId: string;
}

export default function ForcedClosureDamagesSection({ reportId }: Props) {
  const { getToken } = useAuth();
  const [result, setResult] = useState<PhasedPreviewResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [absent, setAbsent] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!reportId) return;
      setLoading(true);
      setError(null);
      setAbsent(false);
      try {
        const token = await getToken();
        const resp = await axios.get(
          `${BACKEND_URL}/api/v1/future-closure/phased-output/${reportId}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        if (!cancelled) setResult(resp.data);
      } catch (err: any) {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setAbsent(true); // non-FC or not yet analyzed → hide the section
        } else {
          setError(err?.response?.data?.detail || 'Could not load forced-closure damages.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [reportId, getToken]);

  // Hide entirely when there's no forced-closure result for this case.
  if (absent && !loading) return null;

  return (
    <section className="rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'var(--color-needs-human-tint)' }}>
          <Scale className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
        </div>
        <div>
          <h2 className="cs-h3">Forced-Closure Damages</h2>
          <p className="cs-meta">Deterministic, PV-discounted phased pro-forma — the court-facing forced-closure result</p>
        </div>
      </div>
      <div className="mt-4">
        <PhasedDamagesBreakdown result={result} loading={loading} error={error} />
      </div>
    </section>
  );
}
